import { useMemo } from 'react';
import type { WidgetDataSource, WidgetSpecificConfig } from '@trinity/chat/types/dashboard-config';
import { useDashboardBuilderEditing } from '@trinity/chat-components/containers/Dashboard/context';
import type { CellContainer, CellDrag } from './cellDrag';

export type CellOps = {
  /** Drops `drag` at (row, slot) of this cell; whatever sits there moves down a row. */
  drop: (drag: CellDrag, toRow: number, toSlot: number) => void;
  removeWidget: (row: number, slot: number) => void;
  removeSection: (sectionId: string) => void;
  setWidgetSpan: (row: number, slot: number, span: number) => void;
  setSectionSpan: (sectionId: string, span: number) => void;
  addWidget: (
    row: number,
    slot: number,
    type: string,
    config: Partial<WidgetSpecificConfig>,
    dataSource?: WidgetDataSource,
  ) => string;
  /** A row's dragged height in px; undefined fits the row to its content again. */
  setRowHeight: (row: number, px: number | undefined) => void;
  /**
   * Starts a height drag on a widget (see beginWidgetHeight in the builder context). Returns the
   * function to call with each dragged height, or undefined to fit the widget to its content again.
   */
  beginHeight: (row: number, slot: number, tracks: number[], gap: number) => (px: number | undefined) => void;
};

/** The builder's block or section operations, for one cell. */
export function useCellOps(container: CellContainer): CellOps {
  const ops = useDashboardBuilderEditing();
  const { blockId, sectionId } = container;

  return useMemo<CellOps>(() => {
    const inSection = sectionId !== undefined;
    return {
      drop: (drag, toRow, toSlot) => {
        if (drag.kind === 'cell-section') {
          if (!inSection && drag.blockId === blockId) ops.moveSection(blockId, drag.row, drag.slot, toRow, toSlot);
          return;
        }
        const from = drag;
        if (from.sectionId === null) {
          if (inSection) ops.moveWidgetFromBlockToSection(from.blockId, from.row, from.slot, blockId, sectionId, toRow, toSlot);
          else ops.moveWidget(from.blockId, from.row, from.slot, blockId, toRow, toSlot);
        } else if (!inSection) {
          ops.moveWidgetFromSectionToBlock(from.blockId, from.sectionId, from.row, from.slot, blockId, toRow, toSlot);
        } else if (from.blockId === blockId && from.sectionId === sectionId) {
          ops.moveWidgetInSection(blockId, sectionId, from.row, from.slot, toRow, toSlot);
        } else {
          ops.moveWidgetBetweenSections(from.blockId, from.sectionId, from.row, from.slot, blockId, sectionId, toRow, toSlot);
        }
      },
      removeWidget: (row, slot) =>
        inSection ? ops.removeWidgetFromSection(blockId, sectionId, row, slot) : ops.removeWidgetAtSlot(blockId, row, slot),
      removeSection: (id) => ops.removeSection(blockId, id),
      setWidgetSpan: (row, slot, span) =>
        inSection ? ops.updateSectionWidgetSpan(blockId, sectionId, row, slot, span) : ops.updateWidgetSpan(blockId, row, slot, span),
      setSectionSpan: (id, span) => ops.updateSectionItemSpan(blockId, id, span),
      addWidget: (row, slot, type, config, dataSource) =>
        inSection
          ? ops.assignWidgetToSection(blockId, sectionId, row, slot, type, config, dataSource)
          : ops.assignWidgetToSlot(blockId, row, slot, type, config, dataSource),
      setRowHeight: (row, px) =>
        inSection ? ops.setSectionRowHeight(blockId, sectionId, row, px) : ops.setBlockRowHeight(blockId, row, px),
      beginHeight: (row, slot, tracks, gap) =>
        inSection
          ? ops.beginSectionWidgetHeight(blockId, sectionId, row, slot, tracks, gap)
          : ops.beginWidgetHeight(blockId, row, slot, tracks, gap),
    };
  }, [ops, blockId, sectionId]);
}
